import React, { useState } from 'react';
import { Card, Row, Col, Typography, Button, Tag, Divider, Space, Tabs, Breadcrumb } from 'antd';
import { 
  SearchOutlined, 
  CheckCircleOutlined, 
  ArrowRightOutlined,
  SyncOutlined,
  BarChartOutlined,
  HomeOutlined,
  PlayCircleOutlined,
  RocketOutlined
} from '@ant-design/icons';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { AnimatedSection } from './components/AnimatedSection';
import { theme } from './components/theme';

const { Title, Paragraph, Text } = Typography;
const { TabPane } = Tabs;

const Products = () => {
  const [activeTab, setActiveTab] = useState('all');

  const handleNavigate = (path) => {
    if (path.startsWith('http')) {
      window.location.href = path;
    } else if (path.startsWith('/')) {
      window.open(path, '_self');
    } else {
      const element = document.getElementById(path);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Product data
  const products = [
    {
      id: 'diff-insight',
      name: 'Diff Insight',
      category: 'analytics',
      tagline: 'Advanced Salesforce Comparison Tool',
      description: 'Advanced data comparison and analysis tool that provides actionable insights while maintaining enterprise-grade security standards. Compare Salesforce orgs, track changes, and ensure data integrity with zero data storage.',
      icon: <SearchOutlined />,
      primaryColor: theme.colors.primary.blue,
      link: 'https://diffinsight.mind-mesh.com',
      features: [
        'Real-time Salesforce Org Comparison',
        'Zero Data Storage Architecture',
        'Enterprise Security Standards',
        'Detailed Change Reports',
        'Multi-Org Analysis',
        'Custom Field Mapping'
      ],
      useCases: [
        'Pre/Post Deployment Validation',
        'Org Synchronization',
        'Compliance Auditing',
        'Change Management'
      ],
      pricing: {
        starter: { price: '$99', period: '/month', features: ['Up to 2 orgs', 'Basic reports', 'Email support'] },
        professional: { price: '$299', period: '/month', features: ['Up to 10 orgs', 'Advanced analytics', 'Priority support'] },
        enterprise: { price: 'Custom', period: '', features: ['Unlimited orgs', 'Custom integrations', 'Dedicated success manager'] }
      }
    },
    {
      id: 'rca-migration',
      name: 'RCA Migration Tool',
      category: 'migration',
      tagline: 'Seamless CPQ to RCA Migration',
      description: 'Seamless migration solution between CPQ and RCA systems with automated workflows and comprehensive data integrity checks. Minimize downtime and ensure 100% data accuracy during your migration process.',
      icon: <SyncOutlined />,
      primaryColor: theme.colors.primary.orange,
      link: '/RcaMigration',
      features: [
        'Automated Migration Workflows',
        'Data Integrity Validation',
        'Zero Downtime Migration',
        'Rollback Capabilities',
        'Pre-migration Analysis',
        'Post-migration Verification'
      ],
      useCases: [
        'CPQ System Modernization',
        'Legacy System Migration',
        'Data Consolidation',
        'Platform Upgrades'
      ],
      pricing: {
        starter: { price: '$499', period: '/project', features: ['Up to 10K records', 'Standard validation', 'Basic support'] },
        professional: { price: '$1,999', period: '/project', features: ['Up to 100K records', 'Advanced validation', 'Migration consulting'] },
        enterprise: { price: 'Custom', period: '', features: ['Unlimited records', 'Custom workflows', 'Dedicated migration team'] }
      }
    },
    {
      id: 'crm-platform',
      name: 'CRM Platform',
      category: 'crm',
      tagline: 'Complete Customer Management Solution',
      description: 'Comprehensive customer relationship management platform designed to streamline operations and drive business growth. Built for modern teams with advanced analytics and seamless integrations.',
      icon: <BarChartOutlined />,
      primaryColor: theme.colors.semantic.success,
      link: '/CRM',
      features: [
        'Advanced Sales Pipeline',
        'Customer Analytics Dashboard',
        'Team Collaboration Tools',
        'Automated Workflows',
        'Mobile-First Design',
        '500+ App Integrations'
      ],
      useCases: [
        'Sales Process Optimization',
        'Customer Relationship Management',
        'Sales Performance Tracking',
        'Lead Management'
      ],
      pricing: {
        starter: { price: '$29', period: '/user/month', features: ['Basic CRM', 'Email integration', 'Mobile app'] },
        professional: { price: '$79', period: '/user/month', features: ['Advanced analytics', 'Workflow automation', 'API access'] },
        enterprise: { price: '$149', period: '/user/month', features: ['Custom fields', 'Advanced security', 'Dedicated support'] }
      }
    }
  ];

  const categories = [
    { key: 'all', label: 'All Products', count: products.length },
    { key: 'analytics', label: 'Analytics & Insights', count: products.filter(p => p.category === 'analytics').length },
    { key: 'migration', label: 'Migration Tools', count: products.filter(p => p.category === 'migration').length },
    { key: 'crm', label: 'CRM Solutions', count: products.filter(p => p.category === 'crm').length }
  ];

  const filteredProducts = activeTab === 'all' 
    ? products 
    : products.filter(product => product.category === activeTab);

  const ProductCard = ({ product }) => (
    <Card
      hoverable
      style={{
        height: '100%',
        borderRadius: theme.borderRadius.lg,
        border: `1px solid ${theme.colors.neutral.gray200}`,
        boxShadow: theme.shadows.sm,
      }}
      bodyStyle={{ padding: theme.spacing.xl }}
    >
      <div style={{ textAlign: 'center', marginBottom: theme.spacing.lg }}>
        <div
          style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: `${product.primaryColor}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto',
            marginBottom: theme.spacing.md,
            fontSize: '32px',
            color: product.primaryColor,
          }}
        >
          {product.icon}
        </div>
        <Title level={3} style={{ marginBottom: theme.spacing.sm }}>
          {product.name}
        </Title>
        <Text type="secondary" style={{ fontSize: theme.typography.fontSize.lg }}>
          {product.tagline}
        </Text>
      </div>

      <Paragraph style={{ marginBottom: theme.spacing.lg, textAlign: 'center' }}>
        {product.description}
      </Paragraph>

      <Divider />

      <div style={{ marginBottom: theme.spacing.lg }}>
        <Title level={5} style={{ marginBottom: theme.spacing.md }}>
          Key Features
        </Title>
        <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
          {product.features.slice(0, 4).map((feature, index) => (
            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
              <CheckCircleOutlined style={{ color: product.primaryColor, fontSize: '16px' }} />
              <Text>{feature}</Text>
            </div>
          ))}
          {product.features.length > 4 && (
            <Text type="secondary" style={{ marginLeft: '24px' }}>
              +{product.features.length - 4} more features
            </Text>
          )}
        </div>
      </div>

      <div style={{ marginBottom: theme.spacing.lg }}>
        <Title level={5} style={{ marginBottom: theme.spacing.md }}>
          Popular Use Cases
        </Title>
        <Space wrap>
          {product.useCases.map((useCase, index) => (
            <Tag key={index} color={product.primaryColor.includes('blue') ? 'blue' : product.primaryColor.includes('orange') ? 'orange' : 'green'}>
              {useCase}
            </Tag>
          ))}
        </Space>
      </div>

      <div style={{ textAlign: 'center' }}>
        <Button
          type="primary"
          size="large"
          icon={<ArrowRightOutlined />}
          style={{
            backgroundColor: product.primaryColor,
            borderColor: product.primaryColor,
            width: '100%',
            marginBottom: theme.spacing.md,
          }}
          onClick={() => handleNavigate(product.link)}
        >
          Explore {product.name}
        </Button>
        <Button
          type="default"
          size="large"
          style={{ width: '100%' }}
          onClick={() => scrollToSection(`${product.id}-pricing`)}
        >
          View Pricing
        </Button>
      </div>
    </Card>
  );

  const PricingCard = ({ plan, product, isPopular = false }) => (
    <Card
      style={{
        height: '100%',
        borderRadius: theme.borderRadius.lg,
        border: isPopular ? `2px solid ${product.primaryColor}` : `1px solid ${theme.colors.neutral.gray200}`,
        boxShadow: isPopular ? theme.shadows.xl : theme.shadows.sm,
        position: 'relative',
      }}
      bodyStyle={{ padding: theme.spacing.xl }}
    >
      {isPopular && (
        <div
          style={{
            position: 'absolute',
            top: '-12px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: product.primaryColor,
            color: theme.colors.neutral.white,
            padding: `${theme.spacing.xs} ${theme.spacing.md}`,
            borderRadius: theme.borderRadius.md,
            fontSize: theme.typography.fontSize.sm,
            fontWeight: theme.typography.fontWeight.semibold,
          }}
        >
          Most Popular
        </div>
      )}

      <div style={{ textAlign: 'center', marginBottom: theme.spacing.lg }}>
        <Title level={4} style={{ textTransform: 'capitalize', marginBottom: theme.spacing.sm }}>
          {Object.keys(product.pricing).find(key => product.pricing[key] === plan)}
        </Title>
        <div style={{ marginBottom: theme.spacing.md }}>
          <span style={{ fontSize: theme.typography.fontSize['3xl'], fontWeight: theme.typography.fontWeight.bold }}>
            {plan.price}
          </span>
          <Text type="secondary">{plan.period}</Text>
        </div>
      </div>

      <div style={{ marginBottom: theme.spacing.xl }}>
        {plan.features.map((feature, index) => (
          <div key={index} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: theme.spacing.sm, 
            marginBottom: theme.spacing.sm 
          }}>
            <CheckCircleOutlined style={{ color: product.primaryColor, fontSize: '16px' }} />
            <Text>{feature}</Text>
          </div>
        ))}
      </div>

      <Button
        type={isPopular ? 'primary' : 'default'}
        size="large"
        style={{
          width: '100%',
          backgroundColor: isPopular ? product.primaryColor : undefined,
          borderColor: isPopular ? product.primaryColor : undefined,
        }}
      >
        Get Started
      </Button>
    </Card>
  );

  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: theme.colors.neutral.white,
        fontFamily: theme.typography.fontFamily.base,
      }}
    >
      <Header onNavigate={handleNavigate} />

      <main style={{ paddingTop: '72px' }}>
        {/* Hero Section */}
        <section
          style={{
            background: `linear-gradient(135deg, ${theme.colors.primary.navy} 0%, ${theme.colors.primary.blue} 100%)`,
            padding: `${theme.spacing['4xl']} ${theme.spacing.lg}`,
            textAlign: 'center',
            color: theme.colors.neutral.white,
          }}
        >
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <AnimatedSection animation="fade-up">
              <Title
                level={1}
                style={{
                  color: theme.colors.neutral.white,
                  fontSize: theme.typography.fontSize['5xl'],
                  fontWeight: theme.typography.fontWeight.bold,
                  marginBottom: theme.spacing.md,
                }}
              >
                Enterprise Solutions
                <br />
                <span style={{ color: theme.colors.primary.orange }}>Built for Scale</span>
              </Title>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={100}>
              <Paragraph
                style={{
                  fontSize: theme.typography.fontSize.xl,
                  color: 'rgba(255, 255, 255, 0.9)',
                  marginBottom: theme.spacing['2xl'],
                  maxWidth: '600px',
                  margin: `0 auto ${theme.spacing['2xl']}`,
                }}
              >
                Discover our comprehensive suite of enterprise-grade tools designed to transform your business operations and accelerate growth.
              </Paragraph>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={200}>
              <div
                style={{
                  display: 'flex',
                  gap: theme.spacing.md,
                  justifyContent: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  size="large"
                  type="primary"
                  icon={<RocketOutlined />}
                  style={{
                    backgroundColor: theme.colors.primary.orange,
                    borderColor: theme.colors.primary.orange,
                    padding: '0 32px',
                    height: '48px',
                  }}
                  onClick={() => scrollToSection('products')}
                >
                  Explore Products
                </Button>
                <Button
                  size="large"
                  ghost
                  icon={<PlayCircleOutlined />}
                  style={{
                    padding: '0 32px',
                    height: '48px',
                  }}
                  onClick={() => scrollToSection('comparison')}
                >
                  Compare Solutions
                </Button>
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* Breadcrumb */}
        <section style={{ padding: `${theme.spacing.lg} ${theme.spacing.lg} 0` }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <Breadcrumb>
              <Breadcrumb.Item href="/">
                <HomeOutlined /> Home
              </Breadcrumb.Item>
              <Breadcrumb.Item>Products</Breadcrumb.Item>
            </Breadcrumb>
          </div>
        </section>

        {/* Product Categories */}
        <section id="products" style={{ padding: `${theme.spacing.xl} ${theme.spacing.lg}` }}>
          <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
            <AnimatedSection animation="fade-up">
              <Title level={2} style={{ textAlign: 'center', marginBottom: theme.spacing.xl }}>
                Our Product Suite
              </Title>
            </AnimatedSection>

            <AnimatedSection animation="fade-up" delay={100}>
              <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                centered
                size="large"
                style={{ marginBottom: theme.spacing['2xl'] }}
              >
                {categories.map(category => (
                  <TabPane
                    tab={
                      <span>
                        {category.label}
                        <Tag 
                          color="blue" 
                          style={{ marginLeft: theme.spacing.xs }}
                        >
                          {category.count}
                        </Tag>
                      </span>
                    }
                    key={category.key}
                  />
                ))}
              </Tabs>
            </AnimatedSection>

            <Row gutter={[32, 32]}>
              {filteredProducts.map((product, index) => (
                <Col xs={24} lg={8} key={product.id}>
                  <AnimatedSection animation="fade-up" delay={index * 100}>
                    <ProductCard product={product} />
                  </AnimatedSection>
                </Col>
              ))}
            </Row>
          </div>
        </section>

        {/* Pricing Section */}
        {/* {filteredProducts.map((product, productIndex) => (
          <section
            key={product.id}
            id={`${product.id}-pricing`}
            style={{
              backgroundColor: productIndex % 2 === 0 ? theme.colors.neutral.gray50 : theme.colors.neutral.white,
              padding: `${theme.spacing['4xl']} ${theme.spacing.lg}`,
            }}
          >
            <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
              <AnimatedSection animation="fade-up">
                <div style={{ textAlign: 'center', marginBottom: theme.spacing['2xl'] }}>
                  <Title level={2} style={{ marginBottom: theme.spacing.md }}>
                    {product.name} Pricing
                  </Title>
                  <Paragraph style={{ fontSize: theme.typography.fontSize.lg, maxWidth: '600px', margin: '0 auto' }}>
                    Choose the plan that best fits your organization's needs. All plans include our core features with varying levels of scale and support.
                  </Paragraph>
                </div>
              </AnimatedSection>

              <Row gutter={[32, 32]} justify="center">
                {Object.entries(product.pricing).map(([planName, plan], index) => (
                  <Col xs={24} md={8} key={planName}>
                    <AnimatedSection animation="fade-up" delay={index * 100}>
                      <PricingCard 
                        plan={plan} 
                        product={product} 
                        isPopular={planName === 'professional'} 
                      />
                    </AnimatedSection>
                  </Col>
                ))}
              </Row>
            </div>
          </section>
        ))} */}

        {/* CTA Section */}
        <section
          style={{
            background: `linear-gradient(135deg, ${theme.colors.accent.peach} 0%, ${theme.colors.neutral.white} 100%)`,
            padding: `${theme.spacing['4xl']} ${theme.spacing.lg}`,
            textAlign: 'center',
          }}
        >
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <AnimatedSection animation="fade-up">
              <Title level={2} style={{ marginBottom: theme.spacing.md }}>
                Ready to Transform Your Business?
              </Title>
              <Paragraph
                style={{
                  fontSize: theme.typography.fontSize.lg,
                  marginBottom: theme.spacing['2xl'],
                  maxWidth: '600px',
                  margin: `0 auto ${theme.spacing['2xl']}`,
                }}
              >
                Join thousands of organizations worldwide who trust Aptify to power their digital transformation. Start your free trial today.
              </Paragraph>
              <Space size="large">
                <Button
                  type="primary"
                  size="large"
                  icon={<RocketOutlined />}
                  style={{
                    backgroundColor: theme.colors.primary.orange,
                    borderColor: theme.colors.primary.orange,
                    padding: '0 32px',
                    height: '48px',
                  }}
                >
                  Start Free Trial
                </Button>
                <Button
                  size="large"
                  style={{
                    padding: '0 32px',
                    height: '48px',
                  }}
                  onClick={() => handleNavigate('/help')}
                >
                  Contact Sales
                </Button>
              </Space>
            </AnimatedSection>
          </div>
        </section>
      </main>

      <Footer onNavigate={handleNavigate} />
    </div>
  );
};

export default Products;